import { useState } from "react";
import { 
    Stack, 
    Spinner, 
    SpinnerSize, 
    DefaultButton, 
    PrimaryButton,
    TextField,
    mergeStyles,
    Icon,
    Dropdown,
    Toggle
} from "@fluentui/react";
import { 
    documentPreprocess
} from "../../../api/admin/api_admin";
import { PreprocessInfo } from "../../../api/admin/models_admin";
import { FileStatus } from "./upload_file_status";
import { buildDefaultArgs } from "../../../utils";

// --- PreprocessUI ---
const preprocessCardStyle = mergeStyles({
    border: '1px solid #e1dfdd',
    borderRadius: 4,
    padding: '12px 16px',
    backgroundColor: '#fff',
    marginTop: 8,
})

const progressBarWrapStyle = mergeStyles({
    height: 6,
    backgroundColor: '#e1dfdd',
    borderRadius: 3,
    overflow: 'hidden',
    margin: '8px 0',
})

const renderSchemaField = (
    key: string,
    schemaProp: any,
    value: any,
    onChange: (k: string, v: any) => void,
    disabled: boolean
) => {
    const label = schemaProp.title ?? key
    const desc  = schemaProp.description ?? ''

    if (schemaProp.enum) {
        const options = (schemaProp.enum as any[]).map((e: any) => ({ key: String(e), text: String(e) }))
        return (
            <Dropdown
                key={key}
                label={label}
                title={desc}
                options={options}
                selectedKey={value !== undefined ? String(value) : options[0]?.key}
                onChange={(_: any, opt: any) => onChange(key, opt?.key)}
                disabled={disabled}
                styles={{ root: { minWidth: 200 } }}
            />
        )
    }
    if (schemaProp.type === 'boolean') {
        return (
            <Toggle
                key={key}
                label={label}
                title={desc}
                checked={!!value}
                onChange={(_: any, checked?: boolean) => onChange(key, !!checked)}
                disabled={disabled}
            />
        )
    }
    if (schemaProp.type === 'integer' || schemaProp.type === 'number') {
        return (
            <TextField
                key={key}
                label={label}
                title={desc}
                type="number"
                value={value !== undefined ? String(value) : ''}
                onChange={(_, v) => onChange(key, v !== '' && v !== undefined ? Number(v) : undefined)}
                disabled={disabled}
                styles={{ root: { width: 200 } }}
            />
        )
    }
    // default: string
    return (
        <TextField
            key={key}
            label={label}
            title={desc}
            value={value ?? ''}
            onChange={(_, v) => onChange(key, v ?? '')}
            disabled={disabled}
            styles={{ root: { minWidth: 200 } }}
            multiline
            rows={5}
        />
    )
}

export const PreprocessUI = (props: {
    info: PreprocessInfo
    filename: string
    containerSeq: number
    onStatusChange: (status: FileStatus) => void
    disabled?: boolean
}) => {
    const { info, filename, containerSeq, onStatusChange, disabled = false } = props
    const schema = info.args_schema
    const hasArgs = Object.keys(schema?.properties ?? {}).length > 0

    const [args, setArgs] = useState<Record<string, any>>(() => Object.fromEntries(buildDefaultArgs(schema)))
    const [running, setRunning] = useState(false)
    const [done, setDone] = useState(false)
    const [progress, setProgress] = useState(0)
    const [resultColor, setResultColor] = useState('#107c10')

    const handleArgChange = (key: string, value: any) => {
        setArgs(prev => ({ ...prev, [key]: value }))
    }

    const handleRun = async () => {
        setRunning(true)
        setProgress(0)
        onStatusChange('preprocessing')
        const success = await documentPreprocess({
            filename,
            container_seq: containerSeq,
            preprocess: info.name,
            args,
            onProgress: (p) => setProgress(p),
        })
        setProgress(100)
        setRunning(false)
        setDone(true)
        if (success) {
            setResultColor('#107c10')
            onStatusChange('completed')
        } else {
            setResultColor('#d13438')
            onStatusChange('preprocessFailed')
        }
    }

    return (
        <div className={preprocessCardStyle}>
            <Stack tokens={{ childrenGap: 6 }}>
                {/* ヘッダ行 */}
                <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <Icon
                            iconName="Processing"
                            styles={{ root: { color: '#0078d4', fontSize: 16 } }}
                        />
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{info.display_name}</span>
                    </Stack>
                    {done && (
                        <Icon
                            iconName={resultColor === '#107c10' ? 'SkypeCircleCheck' : 'ErrorBadge'}
                            styles={{ root: { color: resultColor, fontSize: 16 } }}
                        />
                    )}
                </Stack>

                {/* 説明 */}
                {info.description && (
                    <span style={{ fontSize: 12 }}>{info.description}</span>
                )}

                {/* 注意 */}
                {info.caution && (
                    <span style={{ fontSize: 12, fontWeight: "bold", color: "red" }}>{info.caution}</span>
                )}

                {/* 引数フォーム */}
                {hasArgs && !done && (
                    <Stack tokens={{ childrenGap: 6 }} styles={{ root: { paddingTop: 4 } }}>
                        {Object.entries(schema.properties ?? {}).map(([k, v]) =>
                            renderSchemaField(k, v, args[k], handleArgChange, running || disabled)
                        )}
                    </Stack>
                )}

                {/* プログレスバー */}
                {running && (
                    <div className={progressBarWrapStyle}>
                        <div style={{
                            height: '100%',
                            width: `${progress}%`,
                            backgroundColor: '#0078d4',
                            transition: 'width 0.3s ease',
                        }} />
                    </div>
                )}

                {/* ボタン行 */}
                {!done && (
                    <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end" styles={{ root: { paddingTop: 4 } }}>
                        <PrimaryButton
                            text={running ? `実行中 ${progress}%` : '実行'}
                            iconProps={{ iconName: running ? undefined : 'Play' }}
                            onClick={handleRun}
                            disabled={running || disabled}
                        />
                        {running && <Spinner size={SpinnerSize.small} />}
                    </Stack>
                )}
            </Stack>
        </div>
    )
}
